import React, { useEffect, useState } from "react";
import { onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { db, storage } from "../firebaseConfig";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, getDocs, query, where, doc, getDoc, updateDoc } from "firebase/firestore";
import auth from "./auth";
import placeholderProfilePic from "../profilepic.png";  // Local placeholder image
import back7 from "./back6.png";  // Background image

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userName, setUserName] = useState("");
  const [ownedCars, setOwnedCars] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfilePicture(userData.profilePicture || null);
          setUserName(userData.userName || "Unnamed User");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const fetchOwnedCars = async () => {
      try {
        // Query the investments collection for documents belonging to the user
        const investmentsQuery = query(
          collection(db, "investments"),
          where("userID", "==", user.uid)
        );
        const invSnapshot = await getDocs(investmentsQuery);

        const cars = [];
        // For each investment, fetch the associated car document
        const promises = invSnapshot.docs.map(async (invDoc) => {
          const invData = invDoc.data();
          if (!invData.carID) return;
          const carRef = doc(db, "cars", invData.carID);
          const carDoc = await getDoc(carRef);
          if (carDoc.exists()) {
            cars.push({
              id: carDoc.id,
              ...carDoc.data(),
              amountInvested: invData.amount
            });
          }
        });

        await Promise.all(promises);
        setOwnedCars(cars);
      } catch (error) {
        console.error("Error fetching owned cars:", error);
      }
    };

    fetchUserData();
    fetchOwnedCars();
  }, [user]);

  const handleProfilePictureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileRef = ref(storage, `profilePictures/${user.uid}`);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Error uploading profile picture:", error);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setProfilePicture(downloadURL);

        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { profilePicture: downloadURL });

        setUploadProgress(0);
      }
    );
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return alert("No email found for this user.");
    try {
      await sendPasswordResetEmail(auth, user.email);
      alert("Password reset email sent.");
    } catch (error) {
      console.error("Error sending password reset email:", error);
      alert("Failed to send password reset email.");
    }
  };

  if (!user) {
    return <p>Please log in to access your profile.</p>;
  }

  return (
    <div
      style={{
        backgroundImage: `url(${back7})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        minHeight: "100vh",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h1 style={{ marginBottom: "30px", color: "#fff" }}>Your Profile</h1>

      {/* Profile Picture Section */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "40px" }}>
        <img
          src={profilePicture || placeholderProfilePic}
          alt="Profile"
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid #ccc",
            marginBottom: "10px",
          }}
        />
        <label
          htmlFor="profilePictureUpload"
          style={{ cursor: "pointer", color: "black", marginBottom: "0px" }}
        >
          Upload Profile Picture
        </label>
        <input
          id="profilePictureUpload"
          type="file"
          style={{ display: "none" }}
          onChange={handleProfilePictureUpload}
        />
        {uploadProgress > 0 && (
          <div style={{ color: "#007bff", marginBottom: "0px" }}>
            Uploading: {Math.round(uploadProgress)}%
          </div>
        )}
      </div>

      {/* User Info Section */}
      <div style={{ textAlign: "center", marginBottom: "30px", color: "#fff" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "bold", marginBottom: "10px", color: "white" }}>
          {userName}
        </h2>
        <p>
          <strong>Email:</strong> {user.email}
        </p>
        <button
          onClick={handlePasswordReset}
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginTop: "10px",
          }}
        >
          Reset Password
        </button>
      </div>

      {/* Owned Cars Section */}
      <h2 style={{ marginBottom: "20px", color: "white" }}>Owned Cars</h2>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "20px",
          justifyContent: "center",
        }}
      >
        {ownedCars.length > 0 ? (
          ownedCars.map((car) => (
            <div
              key={car.id}
              style={{
                width: "300px",
                background: "#1e1e1e",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                overflow: "hidden",
                textAlign: "center",
              }}
            >
              <img
                src={car.pictures?.[0] || "https://via.placeholder.com/300x200"}
                alt={car.carName || "Car"}
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "cover",
                }}
              />
              <div style={{ padding: "15px" }}>
                <h3 style={{ marginBottom: "10px", color: "#007bff" }}>
                  {car.carName || "Unnamed Car"}
                </h3>
                <p style={{ marginBottom: "5px" }}>
                  <strong>Total Value:</strong> ${car.totalValue?.toLocaleString() || "N/A"}
                </p>
                <p>
                  <strong>Money Raised:</strong> ${car.moneyRaised?.toLocaleString() || "N/A"}
                </p>
                <p>
                  <strong>Amount Invested:</strong> ${car.amountInvested?.toLocaleString() || "N/A"}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p style={{ textAlign: "center", color: "#555" }}>
            You do not own any cars.
          </p>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
